/**
 * Unified ProseMirror Editor Component
 *
 * A flexible editor component that supports both read-only and writable modes.
 * This is the foundation for all text editing/viewing in Markeroo.
 *
 * Features:
 * - Read-only mode for viewing documents with highlights
 * - Writable mode for student writing portal
 * - Accepts optional plugins for highlights, edit tracking, etc.
 * - Supports initial content as ProseMirror Node
 *
 * Usage:
 * ```tsx
 * // Read-only viewer
 * <Editor initialContent={doc} editable={false} />
 *
 * // Writable editor
 * <Editor initialContent={doc} editable={true} onChange={handleChange} />
 * ```
 */

import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import type { Transaction } from "@tiptap/pm/state";
import { EditorState, Plugin } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";
import { history, undo, redo, undoDepth, redoDepth } from "@tiptap/pm/history";
import { dropCursor } from "@tiptap/pm/dropcursor";
import { keymap } from "@tiptap/pm/keymap";
import { baseKeymap, toggleMark, setBlockType, wrapIn } from "@tiptap/pm/commands";
import { Fragment, type Mark, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import {
  splitListItem,
  liftListItem,
  sinkListItem,
} from "@tiptap/pm/schema-list";

import { schema, createEmptyDoc } from "./schema";
import { blockKeyPlugin } from "./plugins/blockKeyPlugin";
import { SYNC_TRANSACTION_META } from "./plugins/editHistoryPlugin";

export interface EditorProps {
  /**
   * Initial document content. If null/undefined, creates empty document.
   *
   * NOTE: This is an UNCONTROLLED prop - it’s only read during initialization.
   * To programmatically update content after mount, use the `setContent()` ref method.
   *
   * @example
   * const editorRef = useRef<EditorRef>(null);
   * // Later, to update content:
   * editorRef.current?.setContent(newDoc);
   */
  initialContent?: ProseMirrorNode | null;

  /** Whether the editor is editable. Defaults to false (read-only). */
  editable?: boolean;

  /**
   * Additional ProseMirror plugins to include.
   *
   * NOTE: This is an UNCONTROLLED prop - only read during initialization.
   * Plugin changes after mount will NOT take effect. Use `useMemo` to create
   * a stable reference and avoid unnecessary re-renders.
   */
  plugins?: Plugin[];

  /** Called when document content changes. Only fires when editable=true. */
  onChange?: (doc: ProseMirrorNode) => void;

  /** Called when editor gains focus. */
  onFocus?: () => void;

  /** Called when editor loses focus. */
  onBlur?: () => void;

  /**
   * Called when user triggers save shortcut (Cmd/Ctrl+S).
   * If not provided, the shortcut does nothing.
   */
  onSave?: () => void;

  /**
   * Called when user triggers submit shortcut (Cmd/Ctrl+Enter).
   * Typically used to open a submit confirmation modal.
   * If not provided, the shortcut does nothing.
   */
  onSubmit?: () => void;

  /**
   * Called when user presses "?" to show keyboard shortcuts help.
   * If not provided, the shortcut does nothing.
   */
  onShowHelp?: () => void;

  /**
   * Called when text formatting changes (via keyboard shortcuts).
   * Receives the format name (e.g., "Bold", "Italic") and whether it was applied or removed.
   * Useful for screen reader announcements.
   */
  onFormatChange?: (formatName: string, applied: boolean) => void;

  /**
   * Called when user triggers find shortcut (Cmd/Ctrl+F).
   * If not provided, the shortcut does nothing.
   */
  onFind?: () => void;

  /**
   * Called when user triggers find and replace shortcut (Cmd/Ctrl+H).
   * Only relevant in editable mode.
   * If not provided, the shortcut does nothing.
   */
  onFindReplace?: () => void;

  /** Placeholder text shown when document is empty. */
  placeholder?: string;

  /** Additional CSS class for the editor container. */
  className?: string;

  /** Test ID for the editor container. */
  testId?: string;

  /** Auto-focus the editor on mount. */
  autoFocus?: boolean;

  /** ARIA label for accessibility. */
  ariaLabel?: string;
}

export interface EditorRef {
  /** Get the current document content as ProseMirror Node. */
  getContent: () => ProseMirrorNode;

  /** Get plain text content from the document. */
  getText: (options?: { blockSeparator?: string }) => string;

  /**
   * Set new document content.
   *
   * @param content - The new document content
   * @param options - Optional settings
   * @param options.isSync - If true, marks this as a sync operation that should
   *   not be recorded as a user edit event. Use this when loading content from
   *   the server, restoring from backup, or any programmatic content update.
   */
  setContent: (
    content: ProseMirrorNode,
    options?: { isSync?: boolean }
  ) => void;

  /** Focus the editor. */
  focus: () => void;

  /** Blur the editor. */
  blur: () => void;

  /** Get the underlying EditorView instance. */
  getView: () => EditorView | null;

  /** Check if editor is empty. */
  isEmpty: () => boolean;

  /** Check if undo is available. */
  canUndo: () => boolean;

  /** Check if redo is available. */
  canRedo: () => boolean;

  /** Get the number of available undo steps. */
  getUndoDepth: () => number;

  /** Get the number of available redo steps. */
  getRedoDepth: () => number;

  /** Undo the last change. */
  undo: () => boolean;

  /** Redo the last undone change. */
  redo: () => boolean;
}

/**
 * Placeholder plugin that shows placeholder text when the document is empty.
 */
function createPlaceholderPlugin(placeholder: string): Plugin {
  return new Plugin({
    props: {
      // Sets data-placeholder attribute when document is empty.
      // CSS uses this to show placeholder text via ::before pseudo-element.
      attributes(state): { [name: string]: string } {
        const doc = state.doc;
        if (
          doc.childCount === 1 &&
          doc.firstChild?.isTextblock &&
          doc.firstChild.content.size === 0
        ) {
          return { "data-placeholder": placeholder };
        }
        return {};
      },
    },
  });
}

export const Editor = forwardRef<EditorRef, EditorProps>(function Editor(
  {
    initialContent,
    editable = false,
    plugins = [],
    onChange,
    onFocus,
    onBlur,
    onSave,
    onSubmit,
    onShowHelp,
    onFormatChange,
    onFind,
    onFindReplace,
    placeholder,
    className = "",
    testId = "editor",
    autoFocus = false,
    ariaLabel,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const onSaveRef = useRef(onSave);
  const onSubmitRef = useRef(onSubmit);
  const onShowHelpRef = useRef(onShowHelp);
  const onFormatChangeRef = useRef(onFormatChange);
  const onFindRef = useRef(onFind);
  const onFindReplaceRef = useRef(onFindReplace);

  // Keep callback refs up to date
  useEffect(() => {
    onChangeRef.current = onChange;
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;
    onSaveRef.current = onSave;
    onSubmitRef.current = onSubmit;
    onShowHelpRef.current = onShowHelp;
    onFormatChangeRef.current = onFormatChange;
    onFindRef.current = onFind;
    onFindReplaceRef.current = onFindReplace;
  }, [onChange, onFocus, onBlur, onSave, onSubmit, onShowHelp, onFormatChange, onFind, onFindReplace]);

  // Expose editor API via ref
  useImperativeHandle(
    ref,
    () => ({
      getContent: () => {
        return viewRef.current?.state.doc ?? createEmptyDoc();
      },
      getText: (options?: { blockSeparator?: string }) => {
        const view = viewRef.current;
        if (!view) return "";
        const separator = options?.blockSeparator ?? "\n\n";
        const doc = view.state.doc;
        const parts: string[] = [];
        doc.forEach((node) => {
          if (node.isTextblock) {
            parts.push(node.textContent);
          }
        });
        return parts.join(separator);
      },
      setContent: (content: ProseMirrorNode, options?: { isSync?: boolean }) => {
        const view = viewRef.current;
        if (!view) return;

        // Validate schema compatibility
        if (content.type.schema !== schema) {
          console.error(
            "Editor.setContent: content must use the editor schema. " +
              "Use jsonContentToProseMirror() to convert JSONContent."
          );
          return;
        }

        // Handle both doc nodes and other node types
        const fragment =
          content.type.name === "doc" ? content.content : Fragment.from(content);
        let tr = view.state.tr.replaceWith(0, view.state.doc.content.size, fragment);

        // Mark as sync operation if specified - this prevents the edit history
        // plugin from recording this as a user edit event
        if (options?.isSync) {
          tr = tr.setMeta(SYNC_TRANSACTION_META, true);
        }

        view.dispatch(tr);
      },
      focus: () => {
        viewRef.current?.focus();
      },
      blur: () => {
        viewRef.current?.dom.blur();
      },
      getView: () => viewRef.current,
      isEmpty: () => {
        const view = viewRef.current;
        if (!view) return true;
        const doc = view.state.doc;
        return (
          doc.childCount === 1 &&
          doc.firstChild?.isTextblock === true &&
          doc.firstChild.content.size === 0
        );
      },
      canUndo: () => {
        const view = viewRef.current;
        if (!view) return false;
        // Use undoDepth instead of undo command for more reliable state checking
        return undoDepth(view.state) > 0;
      },
      canRedo: () => {
        const view = viewRef.current;
        if (!view) return false;
        // Use redoDepth instead of redo command for more reliable state checking
        return redoDepth(view.state) > 0;
      },
      getUndoDepth: () => {
        const view = viewRef.current;
        if (!view) return 0;
        // prosemirror-history types undoDepth as returning `any`, but it always returns number
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return undoDepth(view.state);
      },
      getRedoDepth: () => {
        const view = viewRef.current;
        if (!view) return 0;
        // prosemirror-history types redoDepth as returning `any`, but it always returns number
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return redoDepth(view.state);
      },
      undo: () => {
        const view = viewRef.current;
        if (!view) return false;
        return undo(view.state, view.dispatch);
      },
      redo: () => {
        const view = viewRef.current;
        if (!view) return false;
        return redo(view.state, view.dispatch);
      },
    }),
    []
  );

  // Dispatch transaction handler
  const dispatchTransaction = useCallback(
    (tr: Transaction) => {
      const view = viewRef.current;
      if (!view) return;

      const newState = view.state.apply(tr);
      view.updateState(newState);

      // Notify parent of content changes
      if (tr.docChanged && onChangeRef.current) {
        onChangeRef.current(newState.doc);
      }
    },
    []
  );

  // Initialize editor on mount
  useEffect(() => {
    if (!containerRef.current) return;

    // Build plugin list
    const allPlugins: Plugin[] = [
      blockKeyPlugin,
      ...plugins,
    ];

    // Add history and keymap for editable mode
    if (editable) {
      // Mark preservation plugin - tracks marks across block splits using plugin state
      const markPreservationPlugin = new Plugin<Mark[] | null>({
        state: {
          init: () => null as Mark[] | null,
          apply: (tr, value) => {
            // If marks are explicitly stored in transaction metadata, use those
            const storedMarks = tr.getMeta('preserveMarks') as Mark[] | null | undefined;
            if (storedMarks !== undefined) {
              return storedMarks;
            }
            // Keep marks until they’re explicitly cleared
            return value;
          }
        },
        appendTransaction: (transactions, oldState, newState) => {
          const pluginState = markPreservationPlugin.getState(newState) as Mark[] | null;

          // If we have preserved marks and the selection is in an empty node, apply them
          if (pluginState && pluginState.length > 0) {
            const { selection } = newState;
            const { $from } = selection;

            // Check if we just typed in an empty block
            const parent = $from.parent;
            if (parent && parent.content.size > 0) {
              // Find text nodes that don’t have the preserved marks
              let needsMarks = false;
              parent.forEach((node) => {
                if (node.isText && !pluginState.every((mark: Mark) => mark.isInSet(node.marks))) {
                  needsMarks = true;
                }
              });

              if (needsMarks) {
                const tr = newState.tr;
                const start = $from.start();
                const end = start + parent.content.size;

                // Apply all preserved marks to the text
                pluginState.forEach((mark: Mark) => {
                  tr.addMark(start, end, mark);
                });

                // Clear the preserved marks after applying
                tr.setMeta('preserveMarks', null);
                return tr;
              }
            }
          }

          return null;
        }
      });

      allPlugins.push(markPreservationPlugin as Plugin);

      // Helper to check if a mark is active at the current selection
      const isMarkActiveInState = (
        state: EditorState,
        markName: string
      ): boolean => {
        const { from, to, empty } = state.selection;
        const markType = schema.marks[markName];
        if (!markType) return false;

        if (empty) {
          return !!markType.isInSet(
            state.storedMarks ?? state.selection.$from.marks()
          );
        }

        let active = true;
        state.doc.nodesBetween(from, to, (node) => {
          if (!node.isText) return;
          if (!markType.isInSet(node.marks)) {
            active = false;
          }
        });
        return active;
      };

      // Human-readable format names for announcements
      const formatNames: Record<string, string> = {
        strong: "Bold",
        em: "Italic",
        underline: "Underline",
      };

      // Create a toggle mark command with announcement
      const createToggleMarkWithAnnouncement = (markName: string) => {
        const markType = schema.marks[markName];
        return (state: EditorState, dispatch?: (tr: Transaction) => void) => {
          const wasActive = isMarkActiveInState(state, markName);
          const command = toggleMark(markType);
          const success = command(state, dispatch);

          if (success && dispatch && onFormatChangeRef.current) {
            const formatName = formatNames[markName] ?? markName;
            onFormatChangeRef.current(formatName, !wasActive);
          }

          return success;
        };
      };

      // Formatting shortcuts (bold, italic, underline)
      const markShortcuts = {
        "Mod-b": createToggleMarkWithAnnouncement("strong"),
        "Mod-i": createToggleMarkWithAnnouncement("em"),
        "Mod-u": createToggleMarkWithAnnouncement("underline"),
      };

      // Block type shortcuts (headings, blockquote, code block, paragraph)
      const headingShortcuts = {
        "Mod-Alt-1": setBlockType(schema.nodes.heading, { level: 1 }),
        "Mod-Alt-2": setBlockType(schema.nodes.heading, { level: 2 }),
        "Mod-Alt-3": setBlockType(schema.nodes.heading, { level: 3 }),
        "Mod-Alt-0": setBlockType(schema.nodes.paragraph),
        "Mod-Alt-q": wrapIn(schema.nodes.blockquote),
        "Mod-Alt-c": setBlockType(schema.nodes.code_block),
      };

      // Custom Enter handler that preserves storedMarks across new lines
      // This allows formatting (bold/italic/underline) to persist when creating new paragraphs
      const handleEnterWithMarkPreservation = (
        state: EditorState,
        dispatch?: (tr: Transaction) => void
      ): boolean => {
        // First check if we’re in a list item - if so, use list-specific behavior
        const { $from } = state.selection;
        for (let d = $from.depth; d >= 0; d--) {
          const node = $from.node(d);
          if (node.type === schema.nodes.list_item) {
            return splitListItem(schema.nodes.list_item)(state, dispatch);
          }
        }

        // Not in a list - use default split block behavior with mark preservation
        const { selection, storedMarks, tr: transaction } = state;

        // Get marks to preserve (either stored marks or marks at current position)
        const marks = storedMarks ?? selection.$from.marks();

        // Split the block (create new paragraph)
        const splitTr = transaction.split(selection.from);

        // Preserve marks for the next typing using both storedMarks and plugin metadata
        if (marks.length > 0) {
          splitTr.setStoredMarks(marks);
          // Also store in plugin metadata so the mark preservation plugin can apply them
          splitTr.setMeta('preserveMarks', marks);
        }

        if (dispatch) {
          dispatch(splitTr.scrollIntoView());
        }

        return true;
      };

      // List handling commands
      const listKeymap = {
        Enter: handleEnterWithMarkPreservation,
        Tab: sinkListItem(schema.nodes.list_item),
        "Shift-Tab": liftListItem(schema.nodes.list_item),
      };

      // Application-level shortcuts (save, submit, escape, help)
      const appShortcuts = {
        // Cmd/Ctrl+S: Manual save
        "Mod-s": () => {
          onSaveRef.current?.();
          return true; // Prevent browser save dialog
        },
        // Cmd/Ctrl+Enter: Submit
        "Mod-Enter": () => {
          onSubmitRef.current?.();
          return true;
        },
        // Escape: Blur editor
        Escape: () => {
          viewRef.current?.dom.blur();
          return true;
        },
        // Shift+/: Show keyboard shortcuts help (? key)
        "Shift-/": () => {
          onShowHelpRef.current?.();
          return true;
        },
        // F1: Alternative keyboard shortcuts help (international keyboard support)
        F1: () => {
          onShowHelpRef.current?.();
          return true;
        },
        // Cmd/Ctrl+F: Find
        "Mod-f": () => {
          onFindRef.current?.();
          return true; // Prevent browser find dialog
        },
        // Cmd/Ctrl+H: Find and replace (edit mode only)
        "Mod-h": () => {
          onFindReplaceRef.current?.();
          return true;
        },
      };

      allPlugins.push(
        history({
          depth: 200, // Increased from 100 to support ~20 minutes of editing
          newGroupDelay: 1000, // Increased from 500ms to 1s for better undo grouping
        }),
        // Drop cursor for drag-and-drop text movement visual feedback
        dropCursor({
          color: "rgb(59, 130, 246)", // blue-500 to match caret color
          width: 2,
        }),
        keymap({ "Mod-z": undo, "Mod-Shift-z": redo, "Mod-y": redo }),
        keymap(markShortcuts),
        keymap(headingShortcuts),
        keymap(listKeymap),
        keymap(appShortcuts),
        keymap(baseKeymap)
      );
    }

    // Add placeholder plugin if provided
    if (placeholder) {
      allPlugins.push(createPlaceholderPlugin(placeholder));
    }

    // Determine initial document
    const doc = initialContent ?? createEmptyDoc();

    // Validate document is using correct schema
    // If it’s from a different schema (e.g., TipTap), we’ll need the adapter
    const docToUse = doc.type.schema === schema ? doc : createEmptyDoc();

    // Create editor state
    const state = EditorState.create({
      doc: docToUse,
      plugins: allPlugins,
    });

    // Create editor view
    const view = new EditorView(containerRef.current, {
      state,
      editable: () => editable,
      dispatchTransaction,
      handleDOMEvents: {
        focus: () => {
          onFocusRef.current?.();
          return false;
        },
        blur: () => {
          onBlurRef.current?.();
          return false;
        },
      },
    });

    viewRef.current = view;

    // Enable native browser spellcheck and autocorrect for editable mode
    // These attributes are especially important for mobile devices (iPad/iPhone)
    // where students rely on iOS spellcheck and autocorrect
    if (editable) {
      view.dom.setAttribute("spellcheck", "true");
      view.dom.setAttribute("autocorrect", "on");
      view.dom.setAttribute("autocapitalize", "sentences");
    }

    // Auto-focus if requested
    if (autoFocus && editable) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        view.focus();
      }, 0);
    }

    // Cleanup on unmount
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [editable, placeholder, autoFocus, dispatchTransaction]); // plugins/initialContent are uncontrolled (init-only)

  // Update editable state without recreating the view
  useEffect(() => {
    const view = viewRef.current;
    if (view) {
      // Force re-evaluation of editable
      view.setProps({ editable: () => editable });

      // Update spellcheck/autocorrect attributes to match editable state
      if (editable) {
        view.dom.setAttribute("spellcheck", "true");
        view.dom.setAttribute("autocorrect", "on");
        view.dom.setAttribute("autocapitalize", "sentences");
      } else {
        view.dom.removeAttribute("spellcheck");
        view.dom.removeAttribute("autocorrect");
        view.dom.removeAttribute("autocapitalize");
      }
    }
  }, [editable]);

  // Handle virtual keyboard viewport changes on mobile
  // This ensures the cursor stays visible when the keyboard opens
  useEffect(() => {
    if (!editable) return;

    const viewport = window.visualViewport;
    if (!viewport) return; // Not supported (older browsers)

    let lastHeight = viewport.height;

    const handleResize = () => {
      const view = viewRef.current;
      if (!view || !view.hasFocus()) return;

      // Detect keyboard opening (viewport height decreased significantly)
      const heightDiff = lastHeight - viewport.height;
      const isKeyboardOpening = heightDiff > 100; // Keyboard typically > 200px

      if (isKeyboardOpening) {
        // Scroll the cursor into view after a short delay
        // to let the viewport settle
        setTimeout(() => {
          const { from } = view.state.selection;
          const coords = view.coordsAtPos(from);

          // Check if cursor is below the visible viewport
          const viewportBottom = viewport.offsetTop + viewport.height;
          if (coords.bottom > viewportBottom - 50) {
            // Scroll the cursor into view with some padding
            const scrollContainer = containerRef.current?.closest(".overflow-auto, .overflow-y-auto") ||
              document.documentElement;
            if (scrollContainer) {
              const scrollAmount = coords.bottom - viewportBottom + 100;
              scrollContainer.scrollTop += scrollAmount;
            }
          }
        }, 100);
      }

      lastHeight = viewport.height;
    };

    viewport.addEventListener("resize", handleResize);

    return () => {
      viewport.removeEventListener("resize", handleResize);
    };
  }, [editable]);

  // Base styles for the editor
  const baseClasses = [
    "tiptap",
    "prose",
    "prose-slate",
    "max-w-none",
    "focus:outline-none",
    editable ? "" : "select-text",
    // Placeholder styles (works with data-placeholder attribute)
    "before:text-theme-muted",
    "before:pointer-events-none",
    "before:float-left",
    "before:h-0",
    "[&[data-placeholder]:before]:content-[attr(data-placeholder)]",
  ].filter(Boolean).join(" ");

  return (
    <div
      ref={containerRef}
      className={`${baseClasses} ${className}`}
      data-testid={testId}
      data-editable={editable}
      aria-label={ariaLabel}
    />
  );
});

export default Editor;
